import { Card } from "@/components/ui/card";
import { formatDistanceToNow, differenceInDays, format } from "date-fns";
import { useRealtimeDeviceDetails } from "@/hooks/use-realtime-device-details";
import { useFleetModeQuota } from "@/hooks/use-fleet-mode-quota";
import { useDeviceContractInfo } from "@/hooks/use-device-contract-info";
import { Button } from "@/components/ui/button";
import { Pencil, WifiOff, BatteryLow, Info, FolderOpen, X, Loader2, Play, CameraOff } from "lucide-react";
import { ImageQuotaPieChart } from "./ImageQuotaPieChart";
import { AIAnalysisDialog } from "./AIAnalysisDialog";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useState } from "react";
import { utcToLocal } from "@/utils/timezoneUtils";
import { useSimpleRole } from "@/hooks/use-simple-role";
import { toast } from "sonner";
import { useCommercialStatus } from "@/hooks/use-commercial-status";
import { CommercialStatusBadges } from "@/components/common/CommercialStatusBadges";
import { useQueryClient } from "@tanstack/react-query";
import { useDeviceImageCapture, areImagesStopped, parseSnapshot, DEFAULT_RESTORE } from "@/hooks/use-device-image-capture";
interface DeviceResponse {
  deviceName: string;
  deviceDescription: string;
  deviceLastUpdated: string;
  deviceHibernate: number;
  notification: {
    result: string;
    message: string;
  };
  imageKit: {
    thumbnailUrl: string;
  };
  location?: {
    map: string;
  };
  currentHdop?: string;
  latWGS84?: number;
  longWGS84?: number;
  contract?: {
    imagesAllowed: number;
    imagesCurrent: number;
    imagesOverflow: number;
  };
  battery?: {
    batterySoC: number;
  };
}

interface DeviceTileProps {
  deviceId: string;
  organizationId?: string | null;
}

// Utility functions for formatting time data
const formatOperatingHours = (scheduleStart?: string | number, scheduleEnd?: string | number, timezone?: string): string => {
  if (scheduleStart === undefined || scheduleEnd === undefined) return 'Always Active';
  
  // Convert strings to numbers and treat as hours (not minutes)
  const startHour = typeof scheduleStart === 'string' ? parseInt(scheduleStart) : scheduleStart;
  const endHour = typeof scheduleEnd === 'string' ? parseInt(scheduleEnd) : scheduleEnd;
  
  if (isNaN(startHour) || isNaN(endHour)) return 'Always Active';
  
  // Convert UTC hours to local time
  try {
    const localStartHour = utcToLocal(startHour, timezone || 'UTC');
    const localEndHour = utcToLocal(endHour, timezone || 'UTC');
    
    // Format hours with leading zero if needed
    const formatHour = (hour: number) => `${hour.toString().padStart(2, '0')}:00`;
    
    return `${formatHour(localStartHour)}-${formatHour(localEndHour)}`;
  } catch (error) {
    console.error('Error converting schedule to local time:', error);
    // Fallback to UTC display
    return `${startHour}:00-${endHour}:00`;
  }
};

const formatCaptureInterval = (intervalMs?: string | number): string => {
  if (!intervalMs) return 'Unknown';
  
  // Convert string to number if needed
  const ms = typeof intervalMs === 'string' ? parseInt(intervalMs) : intervalMs;
  if (isNaN(ms)) return 'Unknown';
  
  const hours = ms / (1000 * 60 * 60);
  if (hours >= 1) {
    return hours === 1 ? '1 hour' : `${hours} hours`;
  }
  
  const minutes = ms / (1000 * 60);
  if (minutes >= 1) {
    return minutes === 1 ? '1 minute' : `${Math.round(minutes)} minutes`;
  }
  
  const seconds = ms / 1000;
  return seconds === 1 ? '1 second' : `${Math.round(seconds)} seconds`;
};

const isOnDemandEnabled = (onDemand?: string | number): boolean => {
  if (onDemand === undefined || onDemand === null) return false;
  const value = typeof onDemand === 'string' ? parseInt(onDemand) : onDemand;
  return value === 1;
};

export const DeviceTile = ({ deviceId, organizationId }: DeviceTileProps) => {
  const { device, isLoading } = useRealtimeDeviceDetails(deviceId);
  const { isFleetMode, totalAllowed, totalUsed } = useFleetModeQuota(organizationId);
  const { totalYears, currentYear, nextRenewalDate, isMultiYear } = useDeviceContractInfo(deviceId);
  const { getFlagsForDevice } = useCommercialStatus();
  const commercialFlags = getFlagsForDevice(deviceId, organizationId);
  const [showAIDialog, setShowAIDialog] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [showStartDialog, setShowStartDialog] = useState(false);
  const queryClient = useQueryClient();
  const { startImages, isBusy: isStarting } = useDeviceImageCapture();
  const { role, isSystemAdmin } = useSimpleRole();
  const canCancelShutdown = role === 'admin' || isSystemAdmin;

  if (isLoading || !device) {
    return (
      <Card className="aspect-square p-4 cursor-pointer animate-pulse">
        <div className="aspect-square bg-muted rounded-md mb-4" />
        <div className="space-y-2">
          <div className="h-4 bg-muted rounded w-3/4" />
          <div className="h-4 bg-muted rounded w-1/2" />
        </div>
      </Card>
    );
  }

  const hasNotification = device.notification?.result?.toLowerCase() === "true";
  const lastUpdatedDate = device.deviceLastUpdated ? new Date(device.deviceLastUpdated) : null;
  const lastUpdatedText = lastUpdatedDate && !isNaN(lastUpdatedDate.getTime())
    ? formatDistanceToNow(lastUpdatedDate, { addSuffix: true })
    : 'Unknown';
  
  // Check for low battery warning
  const hasLowBattery = device.battery?.batterySoC !== undefined && device.battery.batterySoC < 10;
  
  // Determine if this is a demo device from either API or DB field names
  const deviceStatusRaw = (device as any).deviceStatus ?? (device as any).device_status;
  const isDemo = typeof deviceStatusRaw === 'string' && deviceStatusRaw.toLowerCase() === 'demo';
  
  // Demo devices should not time out or be considered offline
  const isOffline = isDemo
    ? false
    : (lastUpdatedDate 
      ? differenceInDays(new Date(), lastUpdatedDate) >= 2
      : false);

  // Extract camera config data for operating hours and capture interval
  const cameraConfig = device.camera_config as any;
  const deviceTimezone = (device as any).timezone || 'UTC';
  const operatingHours = formatOperatingHours(cameraConfig?.scheduleStart, cameraConfig?.scheduleEnd, deviceTimezone);
  const isEventSchedule = Number(cameraConfig?.enableEventSchedule) === 1;
  const captureInterval = formatCaptureInterval(
    isEventSchedule ? cameraConfig?.eventScheduleSleepTime : cameraConfig?.withinScheduleSleepTime
  );
  const onDemandEnabled = isOnDemandEnabled(cameraConfig?.onDemand);

  const forceHibernateRaw = cameraConfig?.forceHibernate;
  const isPendingShutdown =
    (forceHibernateRaw === 1 || forceHibernateRaw === "1") &&
    deviceStatusRaw !== 'Hibernate';

  const handleCancelShutdown = async () => {
    setShowCancelDialog(false);
    setIsCancelling(true);
    try {
      const params = new URLSearchParams();
      params.append("deviceID", deviceId);
      params.append("field", "cameraConfig.forceHibernate");
      params.append("value", "0");
      const response = await fetch("https://i56o7w.buildship.run/updateDevice", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params,
      });
      if (!response.ok) throw new Error("Failed");
      toast.success("Pending power off cancelled");
    } catch (e) {
      toast.error("Failed to cancel pending power off");
    } finally {
      setIsCancelling(false);
    }
  };

  // Image capture stopped state (no operational days set)
  const imagesStopped = areImagesStopped((device as any).deviceOperationalDays);
  const stoppedAt = (device as any).images_stopped_at
    ? new Date((device as any).images_stopped_at)
    : null;
  const restoreTarget = parseSnapshot((device as any).stopped_config_snapshot) ?? DEFAULT_RESTORE;
  const hasSnapshot = !!parseSnapshot((device as any).stopped_config_snapshot);

  const handleStartImages = async () => {
    setShowStartDialog(false);
    const restored = await startImages(deviceId, {
      snapshot: (device as any).stopped_config_snapshot,
      cameraConfig: device.camera_config,
    });
    if (restored) {
      queryClient.invalidateQueries({ queryKey: ["device-details", deviceId] });
    }
  };

  return (
    <>
    <Card
      className={`brand-device-card aspect-square cursor-pointer transition-colors relative flex flex-col border border-input ${
        hasNotification ? "brand-device-alert" : "hover:bg-accent"
      }`}
    >
      {/* Header section with edit button */}
      <div className="absolute right-2 top-2 z-20">
        <Button
          variant="secondary"
          size="sm"
          aria-label="Edit device settings"
          className="brand-device-edit bg-primary text-primary-foreground hover:bg-primary/90"
        >
          <Pencil className="h-3 w-3" />
        </Button>
      </div>
      
      {/* Image section - expanded */}
      <div className="h-[60%] p-3 pb-1 relative">
        {isSystemAdmin && (
          <div className="absolute left-1/2 top-4 -translate-x-1/2 z-20">
            <CommercialStatusBadges flags={commercialFlags} size="sm" />
          </div>
        )}
        {device.imageKit?.thumbnailUrl ? (
          <div className="h-full relative">
            <img
              src={device.imageKit.thumbnailUrl}
              alt={device.deviceName}
              className="h-full w-full object-cover rounded-md"
              loading="lazy"
            />
            
            {/* Offline status overlay */}
            {isOffline && (
              <div className="absolute inset-0 bg-black/40 rounded-md flex flex-col items-center justify-center backdrop-blur-[2px]">
                <WifiOff className="h-8 w-8 text-white drop-shadow-lg" />
                <span className="mt-2 px-3 py-1 bg-red-500 text-white text-xs font-semibold rounded-full shadow-lg">
                  OFFLINE
                </span>
              </div>
            )}

            {/* Pending shutdown overlay */}
            {isPendingShutdown && (
              <div className="absolute inset-0 bg-black/30 rounded-md flex items-center justify-center z-20">
                <div className="flex items-center gap-2 px-3 py-2 bg-amber-500 text-white rounded-full shadow-lg">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="text-xs font-bold tracking-wide">PENDING SHUTDOWN</span>
                  {canCancelShutdown && (
                    <button
                      onClick={(e) => { e.stopPropagation(); setShowCancelDialog(true); }}
                      disabled={isCancelling}
                      className="ml-1 p-0.5 rounded-full bg-white/20 hover:bg-white/40 transition-colors disabled:opacity-50"
                      aria-label="Cancel pending power off"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              </div>
            )}
            
            {/* Overlay pie chart on bottom right */}
            <div className="absolute bottom-2 right-2 z-10">
              <ImageQuotaPieChart 
                imagesAllowed={isFleetMode ? totalAllowed : (device.contract?.imagesAllowed || 0)} 
                imagesCurrent={isFleetMode ? totalUsed : (device.contract?.imagesCurrent || 0)}
                isFleetMode={isFleetMode}
                contractYears={isMultiYear ? totalYears : undefined}
                currentYear={isMultiYear ? currentYear : undefined}
                nextRenewalDate={nextRenewalDate}
              />
            </div>
            {/* AI Query Info Icon - top left - Always visible */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowAIDialog(true);
              }}
               className="absolute top-2 left-2 p-1.5 bg-component-background/90 hover:bg-component-background rounded-full shadow-md transition-colors z-10"
              aria-label={hasNotification ? "AI Alert - View details" : "View AI analysis"}
            >
              <Info className={`h-4 w-4 ${hasNotification ? 'text-red-600' : 'text-blue-600'}`} />
            </button>
          </div>
        ) : (
          <div className="h-full bg-muted rounded-md flex items-center justify-center relative">
            {isOffline ? (
              <>
                <WifiOff className="h-8 w-8 text-gray-400" />
                <span className="absolute mt-16 px-3 py-1 bg-red-500 text-white text-xs font-semibold rounded-full">
                  OFFLINE
                </span>
              </>
            ) : (
              <span className="text-xs text-gray-400">No Image</span>
            )}
            {/* Overlay pie chart on bottom right */}
            <div className="absolute bottom-2 right-2">
              <ImageQuotaPieChart 
                imagesAllowed={isFleetMode ? totalAllowed : (device.contract?.imagesAllowed || 0)} 
                imagesCurrent={isFleetMode ? totalUsed : (device.contract?.imagesCurrent || 0)}
                isFleetMode={isFleetMode}
                contractYears={isMultiYear ? totalYears : undefined}
                currentYear={isMultiYear ? currentYear : undefined}
                nextRenewalDate={nextRenewalDate}
              />
            </div>
            {/* AI Query Info Icon - top left */}
            {hasNotification && device.notification?.message && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowAIDialog(true);
                }}
                className="absolute top-2 left-2 p-1.5 bg-white/90 hover:bg-white rounded-full shadow-md transition-colors z-10"
                aria-label="View AI analysis"
              >
                <Info className="h-4 w-4 text-blue-600" />
              </button>
            )}
          </div>
        )}
      </div>
      
      {/* Content section - expanded to fill space */}
      <div className="flex-1 p-3 space-y-2 flex flex-col">
        {/* Header section - Name with tooltip for description */}
        <div className="brand-device-name rounded-md p-3 bg-component-background border border-border">
          <Tooltip>
            <TooltipTrigger asChild>
              <h3 className="text-sm font-bold leading-tight truncate cursor-help text-foreground">
                {device.deviceName || "Unnamed Device"}
              </h3>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-xs">
              <p className="text-sm">{device.deviceDescription || "No description available"}</p>
            </TooltipContent>
          </Tooltip>
        </div>
        
        {/* Group badge - own row */}
        <div className="flex items-center justify-center gap-2 px-2 py-1">
          <FolderOpen className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
          <span className="text-sm font-medium text-muted-foreground truncate">
            {(!device.groupName || device.groupName.toLowerCase() === 'none') ? "No Group" : device.groupName}
          </span>
        </div>
        
        {/* Configuration section - unified green container */}
        <div className="brand-device-metrics border border-border rounded-md px-3 py-1 flex-1 flex items-center bg-component-background">
          <div className="grid grid-cols-3 gap-2 text-center w-full">
            <div className="space-y-1">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Schedule</p>
              <p className="text-xs font-semibold text-foreground leading-tight">{operatingHours}</p>
            </div>
            <div className="space-y-1">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{isEventSchedule ? "Event" : "Interval"}</p>
              <p className="text-xs font-semibold text-foreground leading-tight">{captureInterval}</p>
            </div>
            <div className="space-y-1">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Status</p>
              <p 
                className={`text-xs font-semibold leading-tight ${
                  (isOffline && deviceStatusRaw !== 'Hibernate')
                    ? 'brand-alert-chip bg-red-100 rounded px-2 py-1 text-black'
                    : (deviceStatusRaw === 'Record' || deviceStatusRaw === 'Retry')
                      ? 'bg-green-100 rounded px-2 py-1 text-black' 
                      : (deviceStatusRaw === 'RecordOnDemand' || deviceStatusRaw === 'RetryOnDemand')
                        ? 'bg-blue-100 rounded px-2 py-1 text-black'
                        : deviceStatusRaw === 'Pause'
                          ? 'bg-orange-100 rounded px-2 py-1 text-black'
                        : deviceStatusRaw === 'Hibernate'
                          ? 'rounded px-2 py-1 text-white'
                          : deviceStatusRaw === 'Demo'
                            ? 'bg-yellow-100 rounded px-2 py-1 text-black'
                            : 'text-foreground'
                }`}
                style={deviceStatusRaw === 'Hibernate' ? { backgroundColor: 'hsl(var(--foreground))' } : undefined}
              >
                {(isOffline && deviceStatusRaw !== 'Hibernate')
                  ? 'Offline'
                  : (deviceStatusRaw === 'RecordOnDemand' || deviceStatusRaw === 'RetryOnDemand') 
                    ? 'On Demand' 
                    : deviceStatusRaw === 'Record'
                      ? 'Active'
                      : (deviceStatusRaw || 'Unknown')}
              </p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Status section - Fixed at bottom */}
      <div className="brand-device-footer mt-auto px-3 py-2 border-t bg-muted/50">
        <div className="space-y-0">
          <p className={`text-xs font-medium leading-tight ${
            isDemo ? 'text-blue-600' : (isOffline ? 'text-red-600' : 'text-green-600')
          }`}>
            {isDemo ? 'DEMO' : (<>{isOffline ? 'OFFLINE' : 'ONLINE'} • Last updated {lastUpdatedText}</>)}
          </p>
          
          {/* Absolute timestamp */}
          {!isDemo && lastUpdatedDate && !isNaN(lastUpdatedDate.getTime()) && (
            <p className="text-xs text-muted-foreground leading-tight">
              {format(lastUpdatedDate, 'dd MMM yyyy, HH:mm')}
            </p>
          )}
          
          {hasNotification && (
            <p className="text-xs brand-alert-chip text-red-600 font-medium bg-red-50 px-2 py-1 rounded leading-tight flex items-center justify-center gap-1">
              <Info className="h-3 w-3" />
              Alert - {(device.notification as any)?.subject || 'Click info icon for details'}
            </p>
          )}

          {imagesStopped && (
            <div className="text-xs font-medium bg-amber-100 text-amber-900 px-2 py-1 rounded leading-tight flex items-center justify-between gap-2">
              <span className="flex items-center gap-1 truncate">
                <CameraOff className="h-3 w-3 flex-shrink-0" />
                Images stopped{stoppedAt && !isNaN(stoppedAt.getTime()) ? ` ${format(stoppedAt, 'dd MMM yyyy')}` : ''}
              </span>
              {canCancelShutdown && (
                <button
                  onClick={(e) => { e.stopPropagation(); setShowStartDialog(true); }}
                  disabled={isStarting}
                  className="flex items-center gap-1 px-2 py-0.5 rounded bg-green-600 text-white hover:bg-green-700 transition-colors disabled:opacity-50 flex-shrink-0"
                  aria-label="Start images on this device"
                >
                  {isStarting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Play className="h-3 w-3" />}
                  Start
                </button>
              )}
            </div>
          )}
          
          {hasLowBattery && (
            <p className="text-xs text-red-600 font-medium line-clamp-1 bg-red-50 px-2 py-1 rounded leading-tight flex items-center gap-1">
              <BatteryLow className="h-3 w-3 text-red-600" />
              Low battery: {device.battery?.batterySoC}%
            </p>
          )}
          
          {!hasNotification && device.on_demand_user_id && device.on_demand_user_id !== "NULL" && (
            <p className="text-xs text-blue-600 font-medium line-clamp-1 bg-blue-50 px-2 py-1 rounded leading-tight">
              On Demand Image
            </p>
          )}
        </div>
      </div>

    </Card>
    <AIAnalysisDialog device={device} open={showAIDialog} onOpenChange={setShowAIDialog} />
    <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
      <AlertDialogContent onClick={(e) => e.stopPropagation()}>
        <AlertDialogHeader>
          <AlertDialogTitle>Cancel pending power off?</AlertDialogTitle>
          <AlertDialogDescription>
            Cancel the queued shutdown for {device.deviceName || "this device"}? The camera will remain active.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={(e) => e.stopPropagation()}>Keep pending</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => { e.stopPropagation(); handleCancelShutdown(); }}
            className="bg-primary text-primary-foreground hover:bg-primary/90"
          >
            Cancel power off
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    <AlertDialog open={showStartDialog} onOpenChange={setShowStartDialog}>
      <AlertDialogContent onClick={(e) => e.stopPropagation()}>
        <AlertDialogHeader>
          <AlertDialogTitle>Restart images on {device.deviceName || "this device"}?</AlertDialogTitle>
          <AlertDialogDescription>
            {hasSnapshot
              ? "This restores the settings saved when images were stopped:"
              : "No saved settings were found for this device, so these defaults will be applied:"}
            <span className="mt-2 block text-foreground">
              Days: {restoreTarget.operational_days.split(",").join(", ")}
              <br />
              Schedule: {String(restoreTarget.scheduleStart).padStart(2, "0")}:00–
              {String(restoreTarget.scheduleEnd).padStart(2, "0")}:00 UTC
              <br />
              Interval: {Math.round(restoreTarget.withinScheduleSleepTime / 60000)} minutes
            </span>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={(e) => e.stopPropagation()}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => { e.stopPropagation(); handleStartImages(); }}
            className="bg-green-600 text-white hover:bg-green-700"
          >
            Start images
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
};
